"use client";

import { detectWebviewOrInAppBrowser } from "@/lib/utils";
import {
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  Suspense,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";
import { usePathname } from "next/navigation";
import { AppProgressBar as ProgressBar } from "next-nprogress-bar";
import { Session } from "next-auth";

type MainLayoutContextType = {
  renderMiddleContent?: JSX.Element;
  setRenderMiddleContent?: Dispatch<SetStateAction<JSX.Element | undefined>>;
  logoOnlyInheader?: boolean;
  setLogoOnlyInHeader?: Dispatch<SetStateAction<boolean>>;
  initialLoading?: boolean;
  isWebView?: boolean;
  isWebViewLoaded?: boolean;
  isDealsPage?: boolean;
  setIsDealsPage?: Dispatch<SetStateAction<boolean>>;
  isSuitesPage?: boolean;
  setIsSuitesPage?: Dispatch<SetStateAction<boolean>>;
};

const MainLayoutContext = createContext<MainLayoutContextType>({
  initialLoading: true,
});

export default function MainLayoutProvider({
  children,
  session,
}: PropsWithChildren<{ session: Session | null }>) {
  const pathname = usePathname();
  const [renderMiddleContent, setRenderMiddleContent] = useState<JSX.Element>();
  const [isWebView, setIsWebView] = useState(true); // Default to true until we know otherwise
  const [isWebViewLoaded, setIsWebViewLoaded] = useState(false);
  const [logoOnlyInheader, setLogoOnlyInHeader] = useState(false);
  const [isDealsPage, setIsDealsPage] = useState(false);
  const [isSuitesPage, setIsSuitesPage] = useState(false);

  useEffect(() => {
    setIsWebView(detectWebviewOrInAppBrowser());
    setIsWebViewLoaded(true);
  }, [session?.user.id]);

  // Clear header middle content on route change so it doesn't persist when navigating via menu (e.g. Search -> Trips)
  useEffect(() => {
    setRenderMiddleContent(undefined);
  }, [pathname]);

  return (
    <MainLayoutContext.Provider
      value={{
        renderMiddleContent,
        setRenderMiddleContent,
        isWebView,
        isWebViewLoaded,
        logoOnlyInheader,
        setLogoOnlyInHeader,
        isDealsPage,
        setIsDealsPage,
        isSuitesPage,
        setIsSuitesPage,
      }}
    >
      <Suspense>
        <ProgressBar
          height="3px"
          color={"hsl(var(--brand-gold))"}
          options={{ showSpinner: false }}
          shallowRouting
        />
      </Suspense>
      {children}
    </MainLayoutContext.Provider>
  );
}

export const useMainLayout = () => {
  const {
    renderMiddleContent,
    setRenderMiddleContent,
    isWebView,
    isWebViewLoaded,
    logoOnlyInheader,
    setLogoOnlyInHeader,
    isDealsPage,
    setIsDealsPage,
    isSuitesPage,
    setIsSuitesPage,
  } = useContext(MainLayoutContext);

  return {
    renderMiddleContent,
    setRenderMiddleContent,
    isWebView,
    isWebViewLoaded,
    logoOnlyInheader,
    setLogoOnlyInHeader,
    isDealsPage,
    setIsDealsPage,
    isSuitesPage,
    setIsSuitesPage,
  };
};
